import React, { useMemo } from 'react';
import { RESOLVED_UI_LABELS } from '../config/constants.js';
import AppCanvasPanel from '../workspace/components/AppCanvasPanel.jsx';
import { selectCanvasPatchHighlights } from '../../projection/canvas/canvasPatchHighlightsSelector.js';

function canvasPanelOptions(props) {
  const defaults = {
    activeCanvasDraftEditorValue: '',
    artifactDocument: {},
    canvasActiveTabKey: '',
    canvasAnalysisPending: false,
    canvasCollectedCount: 0,
    canvasContentSettling: false,
    canvasDisplayLines: [],
    canvasDraftEditing: false,
    canvasDraftEditorValue: '',
    canvasEvidenceItems: [],
    canvasEvidenceSummary: {},
    canvasFieldRows: [],
    canvasFocusEvidenceAnchorId: '',
    canvasFullscreenOpen: false,
    canvasManualControlsEnabled: false,
    canvasProgressPercent: 0,
    canvasReportTemplate: null,
    canvasRevisionControls: {},
    canvasRevisionEntries: [],
    canvasTabs: [],
    canvasWorkflowProjection: {},
    isAnalysisTabActive: false,
    resolvedActiveIntakeContent: '',
    resolvedAnalysisContent: '',
    resolvedCanvasAnalysisPayload: null,
    resolvedCanvasEvidenceTabKey: '',
    showCanvasPanel: false,
    sourcingHasMore: false,
    sourcingLoading: false,
    sourcingLoadingMore: false,
    timelineItems: [],
  };
  return {
    ...props,
    ...Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => [
      key, props[key] === undefined ? fallback : props[key],
    ])),
  };
}

export function useCanvasPanelNode(props = {}) {
  const {
    artifactReadState,
    activeCanvasDraftEditorValue,
    artifactDocument,
    canvasActiveTabKey,
    canvasAnalysisPending,
    canvasCollectedCount,
    canvasContentSettling,
    canvasDisplayLines,
    canvasDraftEditing,
    canvasDraftEditorValue,
    canvasEvidenceItems,
    canvasEvidenceSummary,
    canvasFieldRows,
    canvasFocusEvidenceAnchorId,
    canvasFullscreenOpen,
    canvasManualControlsEnabled,
    canvasProgressPercent,
    canvasReportTemplate,
    canvasRevisionControls,
    canvasRevisionEntries,
    canvasTabs,
    canvasWorkflowProjection,
    commitCanvasDraftValue,
    handleCanvasApplyRevision,
    handleCanvasToolbarAction,
    inputState,
    isAnalysisTabActive,
    markCanvasDraftSaved,
    onCanvasSourcingLoadMore,
    resolvedActiveIntakeContent,
    resolvedAnalysisContent,
    resolvedCanvasAnalysisPayload,
    resolvedCanvasEvidenceTabKey,
    setCanvasActiveTabKey,
    setCanvasDraftEditing,
    setCanvasDraftEditorValue,
    setCanvasFullscreenOpen,
    setShowCanvasPanel,
    showCanvasPanel,
    sourcingHasMore,
    sourcingLoading,
    sourcingLoadingMore,
    themeTokens,
    timelineItems,
  } = canvasPanelOptions(props);
  const canvasPatchHighlights = useMemo(
    () => selectCanvasPatchHighlights(timelineItems),
    [timelineItems],
  );
  return useMemo(() => (
    <AppCanvasPanel
      artifactReadState={artifactReadState}
      canvasActiveTabKey={canvasActiveTabKey}
      showCanvasPanel={showCanvasPanel}
      resolvedUILabels={RESOLVED_UI_LABELS}
      canvasFullscreenOpen={canvasFullscreenOpen}
      canvasTabs={canvasTabs}
      resolvedCanvasEvidenceTabKey={resolvedCanvasEvidenceTabKey}
      canvasEvidenceSummary={canvasEvidenceSummary}
      canvasEvidenceItems={canvasEvidenceItems}
      canvasFocusEvidenceAnchorId={canvasFocusEvidenceAnchorId}
      canvasSourcingLoading={sourcingLoading}
      canvasSourcingHasMore={sourcingHasMore}
      canvasSourcingLoadingMore={sourcingLoadingMore}
      onCanvasSourcingLoadMore={onCanvasSourcingLoadMore}
      canvasReportTemplate={canvasReportTemplate}
      canvasAnalysisPending={canvasAnalysisPending}
      canvasContentSettling={canvasContentSettling}
      resolvedCanvasAnalysisPayload={resolvedCanvasAnalysisPayload}
      canvasArtifactDocument={artifactDocument}
      canvasCollectedCount={canvasCollectedCount}
      canvasDisplayLines={canvasDisplayLines}
      canvasFieldRows={canvasFieldRows}
      canvasHighlights={canvasPatchHighlights}
      canvasRevisionEntries={canvasRevisionEntries}
      canvasRevisionControls={canvasRevisionControls}
      canvasDraftEditing={canvasDraftEditing}
      canvasDraftEditorValue={activeCanvasDraftEditorValue}
      canvasManualControlsEnabled={canvasManualControlsEnabled}
      canvasProgressPercent={canvasProgressPercent}
      canvasWorkflowProjection={canvasWorkflowProjection}
      inputState={inputState}
      handleCanvasApplyRevision={handleCanvasApplyRevision}
      setCanvasDraftEditorValue={setCanvasDraftEditorValue}
      handleCanvasCancelEdit={() => {
        setCanvasDraftEditing(false);
        setCanvasDraftEditorValue(isAnalysisTabActive ? resolvedAnalysisContent : resolvedActiveIntakeContent);
      }}
      handleCanvasSaveEdit={async () => {
        const nextValue = String(canvasDraftEditorValue || '').trim();
        const result = await commitCanvasDraftValue(nextValue, { closeEditor: true });
        if (result?.status === 'completed') markCanvasDraftSaved(canvasActiveTabKey, nextValue);
      }}
      handleCanvasStartEdit={() => {
        setCanvasDraftEditorValue(isAnalysisTabActive ? resolvedAnalysisContent : resolvedActiveIntakeContent);
        setCanvasDraftEditing(true);
      }}
      handleCanvasToolbarAction={handleCanvasToolbarAction}
      setCanvasActiveTabKey={setCanvasActiveTabKey}
      setCanvasFullscreenOpen={setCanvasFullscreenOpen}
      setShowCanvasPanel={setShowCanvasPanel}
      themeTokens={themeTokens}
    />
  ), [
    artifactReadState,
    activeCanvasDraftEditorValue,
    artifactDocument,
    canvasActiveTabKey,
    canvasAnalysisPending,
    canvasCollectedCount,
    canvasContentSettling,
    canvasDisplayLines,
    canvasDraftEditing,
    canvasDraftEditorValue,
    canvasEvidenceItems,
    canvasEvidenceSummary,
    canvasFieldRows,
    canvasFocusEvidenceAnchorId,
    canvasFullscreenOpen,
    canvasPatchHighlights,
    canvasManualControlsEnabled,
    canvasProgressPercent,
    canvasReportTemplate,
    canvasRevisionControls,
    canvasRevisionEntries,
    canvasTabs,
    canvasWorkflowProjection,
    commitCanvasDraftValue,
    handleCanvasApplyRevision,
    handleCanvasToolbarAction,
    inputState,
    isAnalysisTabActive,
    markCanvasDraftSaved,
    onCanvasSourcingLoadMore,
    resolvedActiveIntakeContent,
    resolvedAnalysisContent,
    resolvedCanvasAnalysisPayload,
    resolvedCanvasEvidenceTabKey,
    setCanvasActiveTabKey,
    setCanvasDraftEditing,
    setCanvasDraftEditorValue,
    setCanvasFullscreenOpen,
    setShowCanvasPanel,
    showCanvasPanel,
    sourcingHasMore,
    sourcingLoading,
    sourcingLoadingMore,
    themeTokens,
  ]);
}

export default useCanvasPanelNode;
